<Page contentAreaLayout="narrow">
    <PageHeader
        headingLevel="h1"
        i18n={getPageHeaderI18n()}
        title={lang.main("Course_info_box_Course_description")}
        backLinkText={lang.main("BackToX", backToText)}
        backLinkUrl={config.courseUrl}
    />
    <div
        class="itsl-course-info-box itsl-course-info-box__edit-view"
        class:itsl-course-info-box__edit-view--locked={isFormLocked}>
        <CourseInfoBoxEditBanner
            type={bannerType}
            bind:visible={showBanner}
        />
        <Form action="" name="itsl-course-info-box__edit-form">
            <SimpleRtfEditor
                ariaLiveUpdateType="polite"
                errorMessage={lang.main("Course_info_box_Course_description_too_long")}
                {hasError}
                headingLevel={defaultEditorHeadingLevel}
                i18n={getSimpleRtfEditorI18n()}
                label={lang.main("Course_info_box_Description")}
                labelHidden
                languageSelectorEnable={false}
                skiplinkUrl={`#${teacherSectionId}`}
                bind:this={rtfEditor}
                on:change={onEditorChange}
            />

            {#if config.displayShowTeachers}
                <RadioGroup
                    options={showTeachersOptions}
                    name="showTeachers"
                    id={teacherSectionId}
                    label={lang.main("Course_info_box_Show_teachers")}
                    bind:selected={showTeachers}
                    useSmallRadioButtons
                />
            {/if}

            <ButtonGroup>
                <Button
                    disabled={isFormLocked || hasError}
                    htmlType="submit"
                    large
                    loading={isFormLocked}
                    text={lang.main("Save")}
                    type="primary"
                    on:click={onFormSubmit}
                />
                <Button
                    disabled={isFormLocked}
                    htmlType="button"
                    large
                    text={lang.main("Cancel")}
                    type="secondary"
                    on:click={onFormCancel}
                />
            </ButtonGroup>
        </Form>
    </div>
</Page>

<script>
    import { onMount } from "svelte";
    import { post } from "@itslearning/atlas/network/fetch";

    import Button from "@itslearning/prometheus/assets/inputs/Button/v1/Button.svelte";
    import ButtonGroup from "@itslearning/prometheus/assets/inputs/ButtonGroup/v1/ButtonGroup.svelte";
    import Form from "@itslearning/prometheus/assets/inputs/Form/v1/Form.svelte";
    import Page from "@itslearning/prometheus/assets/templates/Page/v1/Page.svelte";
    import PageHeader from "@itslearning/prometheus/assets/patterns/PageHeader/v1/PageHeader.svelte";
    import RadioGroup from "@itslearning/prometheus/assets/inputs/RadioGroup/v1/RadioGroup.svelte";
    import SimpleRtfEditor from "@itslearning/prometheus/assets/nodes/SimpleRtfEditor/v1/SimpleRtfEditor.svelte";

    import CourseInfoBoxEditBanner from "./CourseInfoBoxEditBanner.svelte";
    import {
        courseDescriptionMaxLength,
        courseType,
        defaultEditorHeadingLevel,
        teacherSectionId,
    } from "../../constants";
    import config from "../../config";
    import { isStringLengthValid, setRtfContent } from "../../utils";
    import { lang } from "@its-ui/mono/lang/provider";
    import { getPageHeaderI18n } from "@its-ui/mono/lang/pack/pageHeaderI18n";
    import { getSimpleRtfEditorI18n } from "@its-ui/mono/lang/pack/simpleRtfEditor";

    /** @type {SimpleRtfEditor} */
    let rtfEditor;

    let bannerType = "info";
    let hasError = false;
    let isFormLocked = false;
    let showBanner = false;
    let courseDescription = config.courseDescription;
    let showTeachers = config.showTeachers;

    onMount(() => setRtfContent(rtfEditor, courseDescription));

    $: showTeachersOptions = [
        { label: lang.main("Yes"), id: "yes", value: true, disabled: isFormLocked },
        { label: lang.main("No"), id: "no", value: false, disabled: isFormLocked },
    ];

    $: backToText = config.courseType === courseType.SelfPaced
        ? lang.main("Lesson_planner")
        : lang.main("Overview");

    const displayWarningBanner = () => {
        showBanner = true;
        bannerType = "warning";
    };

    const hideBanner = () => showBanner = false;

    const lockForm = () => isFormLocked = true;
    const unlockForm = () => isFormLocked = false;

    const onEditorChange = () => {
        courseDescription = JSON.stringify(rtfEditor.getData());
        hasError = !isStringLengthValid(courseDescription, courseDescriptionMaxLength);
    };

    const onFormCancel = () => window.location.href = config.courseUrl;
    const onFormSubmit = () => {
        hideBanner();
        lockForm();

        courseDescription = JSON.stringify(rtfEditor.getData());
        hasError = !isStringLengthValid(courseDescription, courseDescriptionMaxLength);

        if (hasError) {
            unlockForm();
            return;
        }

        post(
            config.updateCourseInfoBoxSettingsEndpointURL,
            { courseDescription, showTeachers },
            { headers: { [config.antiForgeryHeaderName]: config.antiForgeryHeaderValue }}
        ).then(() => {
            window.location.href = config.courseUrl;
        }).catch(() => {
            displayWarningBanner();
            unlockForm();
        });
    };
</script>
